import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../context/app";
import {
  AppointmentEvent,
  postAppointmentListView,
} from "../../../apis/appointmentApi";
import { addDays, subMilliseconds } from "date-fns";

/**
 * Fetches appointment schedule in batches
 * If the difference between from and to is more than 7 days, it will fetch in batches of 7 days
 */
export function useBatchAppointmentScheduleQuery(
  {
    from,
    to,
  }: {
    from: string;
    to: string;
  },
  { onError }: { onError?: (error: Error) => void } = {},
) {
  const { client } = useContext(AppContext);

  const [data, setData] = useState<AppointmentEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refetchKey, setRefetchKey] = useState(() => Date.now());
  const onErrorRef = useRef(onError);

  const refetch = useCallback(() => {
    setRefetchKey(Date.now());
  }, []);

  // update the onErrorRef in all renders
  // the onErrorRef is used to avoid adding the onError dependency to the next useEffect
  // Explained here: https://www.epicreact.dev/the-latest-ref-pattern-in-react
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // fetch data in batches if necessary
  useEffect(() => {
    setIsLoading(true);
    setData([]);
    let batchIndex = 0;
    const ranges = createRanges(from, to, 7);
    let continueFetching = true;
    const loadData = async () => {
      if (!continueFetching) {
        return;
      }
      const curInterval = ranges[batchIndex];
      try {
        const response = await postAppointmentListView(
          client!.uuid,
          client.timezone,
          curInterval.from,
          curInterval.to,
        );
        setData((prev) => [...prev, ...response]);
      } catch (error) {
        onErrorRef.current?.(error);
      }
      batchIndex++;
      if (batchIndex < ranges.length) {
        loadData();
      } else {
        setIsLoading(false);
      }
    };
    loadData();
    return () => {
      continueFetching = false;
    };
  }, [from, to, refetchKey, client]);

  return { data, isLoading, refetch };
}

export function createRanges(from: string, to: string, intervalInDays: number) {
  const dateFrom = new Date(from);
  const dateTo = new Date(to);
  const ranges: { from: string; to: string }[] = [];
  let currentFrom = new Date(dateFrom);
  let currentTo = subMilliseconds(addDays(dateFrom, intervalInDays), 1);
  while (currentFrom < dateTo) {
    const to =
      currentTo < dateTo ? currentTo.toISOString() : dateTo.toISOString();
    ranges.push({
      from: currentFrom.toISOString(),
      to,
    });
    currentFrom = addDays(currentFrom, intervalInDays);
    currentTo = addDays(currentTo, intervalInDays);
  }
  return ranges;
}
